/**
 * LeftMenuCalendar — 캘린더 모듈 사이드바 (3 분류 + 시스템)
 *
 * 구성:
 *  1) 미니 달력 (anchorDate 동기화)
 *  2) 필터:
 *     - 🌐 전사 일정      (단일 토글, 그룹 헤더 없이 최상단)
 *     - 🔒 개인 일정      (단일 토글)
 *     - # 내 채널         (그룹 헤더 + 각 채널 토글 + 채널마다 Task 미니 토글)
 *     - 🇰🇷 공휴일       (시스템)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { ChevronLeft, ChevronRight, Lock, Globe, Hash, CalendarCheck, ListTodo, Settings, Share2, Building2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { cn } from '../../lib/utils';
import { useCalendarContext } from '../../context/CalendarContext';
import { useAppUIContext } from '../../context/AppUIContext';
import { isChannelEnabled, resolveHolidayCountries } from '../../models/CalendarEvent';
import { useCalendarLabels } from './useCalendarLabels';
import { fetchAvailableHolidayCountries } from '../../services/CalendarService';
import { My } from '../../services/My';
import { weekdayLabels } from './calendarWeekdays';

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function buildMonthCells(anchor: Date): Date[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - first.getDay());
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

const LeftMenuCalendar: React.FC = () => {
  const { anchorDate, setAnchorDate, filters, setFilters } = useCalendarContext();
  const { activeWorkspaceId, rooms } = useAppUIContext();
  const labels = useCalendarLabels();
  const weekdays = weekdayLabels('narrow');

  const today = new Date();
  const currentMonth = anchorDate.getMonth();
  const cells = useMemo(() => buildMonthCells(anchorDate), [anchorDate]);

  // 본인이 속한 일반 채널 (1:1 / personal 제외) — LeftMenuRooms 와 동일 정렬: roomOrder 우선, 그 외 이름순
  const myChannels = useMemo(() => {
    const list = rooms.filter((r) =>
      r.workspaceId === activeWorkspaceId
      && (r.visibility === 'listed' || r.visibility === 'unlisted')
      && !r.isOneToOne
    );
    const order = My.user.roomOrder ?? [];
    if (order.length === 0) {
      return [...list].sort((a, b) => a.name.localeCompare(b.name));
    }
    const ordered = list
      .filter((r) => order.includes(r.id))
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
    const unordered = list
      .filter((r) => !order.includes(r.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...ordered, ...unordered];
  }, [rooms, activeWorkspaceId]);

  const handlePrevMonth = () => {
    const next = new Date(anchorDate);
    next.setMonth(next.getMonth() - 1);
    setAnchorDate(next);
  };
  const handleNextMonth = () => {
    const next = new Date(anchorDate);
    next.setMonth(next.getMonth() + 1);
    setAnchorDate(next);
  };

  const setFilter = (
    key: 'workspace' | 'personal' | 'holidays' | 'showChannelTasks' | 'showSharedEvents',
    value: boolean,
  ) => {
    setFilters({ ...filters, [key]: value });
  };
  const setChannel = (channelId: string, value: boolean) => {
    setFilters({ ...filters, channels: { ...filters.channels, [channelId]: value } });
  };

  // 전체 시드 국가 풀 (개인이 선택 가능한 국가 — admin 설정 무관)
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchAvailableHolidayCountries()
      .then((pool) => { if (!cancelled) setAvailableCountries(pool); })
      .catch(() => { if (!cancelled) setAvailableCountries([]); });
    return () => { cancelled = true; };
  }, []);

  // 개인이 특정 국가를 보는지 — holidayCountries null 이면 로케일 기본
  const effectiveCountries = resolveHolidayCountries(filters.holidayCountries, i18n.locale);
  const isCountrySelected = (code: string) => effectiveCountries.includes(code);

  const toggleCountry = (code: string, value: boolean) => {
    // null(로케일 기본) 상태에서 토글하면 → 현재 표시 중인 국가를 기준 배열로 고정 후 변경
    let next = value
      ? [...new Set([...effectiveCountries, code])]
      : effectiveCountries.filter((c) => c !== code);
    // 시드 풀 밖 제거
    next = next.filter((c) => availableCountries.includes(c));
    setFilters({ ...filters, holidayCountries: next });
  };

  const monthLabel = `${anchorDate.getFullYear()} / ${String(anchorDate.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">

        {/* 미니 달력 */}
        <div className="mb-4 p-2 rounded-lg bg-muted/50">
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              onClick={handlePrevMonth}
              className="p-0.5 rounded hover:bg-muted cursor-pointer"
              aria-label={t`Previous month`}
            >
              <ChevronLeft className="size-4 text-muted-foreground" />
            </button>
            <span className="text-label font-medium text-foreground">{monthLabel}</span>
            <button
              onClick={handleNextMonth}
              className="p-0.5 rounded hover:bg-muted cursor-pointer"
              aria-label={t`Next month`}
            >
              <ChevronRight className="size-4 text-muted-foreground" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-meta">
            {weekdays.map((d, idx) => (
              <div
                key={idx}
                className={cn(
                  'py-1 font-medium',
                  idx === 0 && 'text-rose-500',
                  idx === 6 && 'text-sky-500',
                  idx !== 0 && idx !== 6 && 'text-muted-foreground',
                )}
              >
                {d}
              </div>
            ))}
            {cells.map((date, idx) => {
              const isCurrentMonth = date.getMonth() === currentMonth;
              const isToday = sameDay(date, today);
              const isSelected = sameDay(date, anchorDate);
              return (
                <button
                  key={idx}
                  onClick={() => setAnchorDate(date)}
                  className={cn(
                    'py-1 rounded cursor-pointer transition-colors',
                    isToday && !isSelected && 'bg-primary/15 text-primary font-semibold',
                    isSelected && 'bg-primary text-primary-foreground font-semibold',
                    !isToday && !isSelected && !isCurrentMonth && 'text-muted-foreground/50 hover:bg-muted',
                    !isToday && !isSelected && isCurrentMonth && 'text-foreground hover:bg-muted',
                  )}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* 전사 일정 — 그룹 헤더 없이 최상단 단일 토글 */}
        <div className="mb-1">
          <FilterRow
            checked={filters.workspace}
            onChange={(v) => setFilter('workspace', v)}
            icon={<Building2 className="size-3.5 text-slate-500" />}
            label={t`Workspace event`}
          />
        </div>

        {/* 개인 일정 — 단일 토글 */}
        <div className="mb-3">
          <FilterRow
            checked={filters.personal}
            onChange={(v) => setFilter('personal', v)}
            icon={<Lock className="size-3.5 text-emerald-500" />}
            label={t`Personal event`}
          />
        </div>

        {/* 내 채널 — 그룹 헤더 + 우측 ⚙ 설정 Popover + 채널별 토글 */}
        {myChannels.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between px-2 mb-1.5">
              <p className="text-meta font-semibold text-muted-foreground uppercase tracking-wider">
                <Trans>My channels</Trans>
              </p>
              <Popover modal>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="size-5 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                    title={t`Channel event settings`}
                    aria-label={t`Channel event settings`}
                  >
                    <Settings className="size-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 p-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-label font-semibold text-foreground mb-1"><Trans>Channel event settings</Trans></p>

                    <label className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer">
                      <Checkbox
                        checked={filters.showSharedEvents}
                        onCheckedChange={(v) => setFilter('showSharedEvents', !!v)}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-label text-foreground flex items-center gap-1.5">
                          <Share2 className="size-3.5 text-primary/70" />
                          <Trans>Others' shared events</Trans>
                        </span>
                        <span className="text-meta text-muted-foreground leading-tight">
                          <Trans>Personal events (vacation, business trips, etc.) that channel members shared to this channel. Shown per enabled channel.</Trans>
                        </span>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer">
                      <Checkbox
                        checked={filters.showChannelTasks}
                        onCheckedChange={(v) => setFilter('showChannelTasks', !!v)}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-label text-foreground flex items-center gap-1.5">
                          <ListTodo className="size-3.5 text-sky-500" />
                          <Trans>Show task deadlines</Trans>
                        </span>
                        <span className="text-meta text-muted-foreground leading-tight">
                          <Trans>Show task deadlines from enabled channels' boards</Trans>
                        </span>
                      </div>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-0.5">
              {myChannels.map((r) => (
                <FilterRow
                  key={r.id}
                  checked={isChannelEnabled(filters, r.id)}
                  onChange={(v) => setChannel(r.id, v)}
                  icon={<Hash className="size-3.5 text-primary" />}
                  label={r.name}
                />
              ))}
            </div>
          </div>
        )}

        {/* 시스템 */}
        <div className="mb-2">
          <p className="text-meta font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1.5">
            <Trans>System</Trans>
          </p>
          <FilterRow
            checked={filters.holidays}
            onChange={(v) => setFilter('holidays', v)}
            icon={<CalendarCheck className="size-3.5 text-red-500" />}
            label={t`Holiday`}
            trailing={
              // 시드된 국가가 있으면 개인 국가 선택 팝오버 노출 (admin 설정 무관)
              availableCountries.length > 0 ? (
                <Popover modal>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="shrink-0 size-5 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                      title={t`Holiday countries`}
                      aria-label={t`Holiday countries`}
                    >
                      <Settings className="size-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-60 p-3">
                    <div className="flex flex-col gap-1">
                      <p className="text-label font-semibold text-foreground mb-1"><Trans>Holiday countries</Trans></p>
                      <p className="text-meta text-muted-foreground mb-1.5">
                        <Trans>Select the countries whose holidays you want to see</Trans>
                      </p>
                      {availableCountries.map((code) => (
                        <label
                          key={code}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={isCountrySelected(code)}
                            onCheckedChange={(v) => toggleCountry(code, !!v)}
                            className="shrink-0"
                          />
                          <span className="text-label text-foreground">{labels.country(code)}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

interface FilterRowProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  trailing?: React.ReactNode;
}

const FilterRow: React.FC<FilterRowProps> = ({ checked, onChange, icon, label, disabled, trailing }) => (
  <div
    className={cn(
      'flex items-center gap-2 px-2 py-1.5 rounded-lg text-body transition-colors',
      disabled ? 'opacity-50' : 'hover:bg-muted/50',
    )}
  >
    {/* label 영역만 클릭 시 토글 — trailing(설정 버튼)은 별도 */}
    <label className={cn('flex items-center gap-2 flex-1 min-w-0', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => !disabled && onChange(!!v)}
        disabled={disabled}
        className="shrink-0"
      />
      {icon}
      <span className={cn('truncate', checked ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
    </label>
    {trailing}
  </div>
);

export default LeftMenuCalendar;
